import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Page } from "../../components";
import LogoutButton from "../../components/LogoutButton";
import { UsageGranularity } from "../../api/admin/types";
import { defaultPeriodForMode, MetricsMode, MetricsTab } from "./shared/constants";
import Toolbar from "./shared/Toolbar";
import PlatformKpiStrip from "./shared/PlatformKpiStrip";
import { isoFromDateInput } from "./shared/constants";
import AgencyTotals from "./AgencyTotals";
import UserTotals from "./UserTotals";
import RoleTotals from "./RoleTotals";
import PolicyInsights from "./PolicyInsights";
import AssistantBubble from "./Assistant/AssistantBubble";

interface AppliedFilters {
  mode: MetricsMode;
  granularity: UsageGranularity;
  startA: string;
  endA: string;
  startB: string;
  endB: string;
  includeTestAccounts: boolean;
}

const MetricsPage: React.FC = () => {
  const navigate = useNavigate();

  const initialDates = defaultPeriodForMode("compare");
  const initialFilters: AppliedFilters = {
    mode: "compare",
    granularity: "week",
    startA: initialDates.startA,
    endA: initialDates.endA,
    startB: initialDates.startB,
    endB: initialDates.endB,
    includeTestAccounts: false,
  };

  // Tab is purely UI; doesn't gate fetches.
  const [tab, setTab] = useState<MetricsTab>("agency");

  // Epic visibility — purely an FE display toggle. BE always returns Epic
  // counts; when off, the FE hides the Epic card on the strip and subtracts
  // it from displayed totals.
  const [showEpic, setShowEpic] = useState(false);

  // Draft = what's in the form. Applied = what the queries actually use.
  // Pressing Apply copies draft -> applied. Mode change auto-applies after
  // resetting dates so the user always sees data for the chosen mode.
  const [draft, setDraft] = useState<AppliedFilters>(initialFilters);
  const [applied, setApplied] = useState<AppliedFilters>(initialFilters);

  const [isFetching, setIsFetching] = useState(false);

  const onApply = useCallback(() => {
    setApplied(draft);
  }, [draft]);

  const onModeChange = useCallback((nextMode: MetricsMode) => {
    const d = defaultPeriodForMode(nextMode);
    const next: AppliedFilters = {
      mode: nextMode,
      granularity: "week",
      startA: d.startA,
      endA: d.endA,
      startB: d.startB,
      endB: d.endB,
      includeTestAccounts: false,
    };
    // Mode swap is auto-applied so the page never sits with stale dates.
    // Preserve the user's existing granularity / test-account toggle.
    setDraft((prev) => ({
      ...next,
      granularity: prev.granularity,
      includeTestAccounts: prev.includeTestAccounts,
    }));
    setApplied((prev) => ({
      ...next,
      granularity: prev.granularity,
      includeTestAccounts: prev.includeTestAccounts,
    }));
  }, []);

  const hasPendingChanges =
    draft.mode !== applied.mode ||
    draft.granularity !== applied.granularity ||
    draft.startA !== applied.startA ||
    draft.endA !== applied.endA ||
    draft.startB !== applied.startB ||
    draft.endB !== applied.endB ||
    draft.includeTestAccounts !== applied.includeTestAccounts;

  return (
    <Page>
      <div
        style={{
          width: "100%",
          maxWidth: 1200,
          margin: "0 auto",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 12,
          padding: "0 16px",
          boxSizing: "border-box",
        }}
      >
        <h1
          style={{
            fontSize: "clamp(28px, 5vw, 40px)",
            margin: 0,
            fontWeight: 600,
            color: "#1a1a1a",
          }}
        >
          Usage Metrics
        </h1>
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <button
            onClick={() => navigate("/")}
            style={{
              padding: "8px 16px",
              borderRadius: 6,
              border: "none",
              background: "#6b7280",
              color: "#fff",
              fontSize: 14,
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            Back to Home
          </button>
          <LogoutButton />
        </div>
      </div>

      <PlatformKpiStrip
        // In compare mode the page is anchored on period B (the "now"), so
        // the KPI strip shows that window. In single mode there's only period A.
        startDate={
          applied.mode === "compare"
            ? isoFromDateInput(applied.startB)
            : isoFromDateInput(applied.startA)
        }
        endDate={
          applied.mode === "compare"
            ? isoFromDateInput(applied.endB)
            : isoFromDateInput(applied.endA)
        }
        includeTestAccounts={applied.includeTestAccounts}
        showEpic={showEpic}
      />

      <Toolbar
        tab={tab}
        setTab={setTab}
        mode={draft.mode}
        onModeChange={onModeChange}
        draftGranularity={draft.granularity}
        draftStartA={draft.startA}
        draftEndA={draft.endA}
        draftStartB={draft.startB}
        draftEndB={draft.endB}
        draftIncludeTestAccounts={draft.includeTestAccounts}
        setDraftGranularity={(g) => setDraft((d) => ({ ...d, granularity: g }))}
        setDraftStartA={(v) => setDraft((d) => ({ ...d, startA: v }))}
        setDraftEndA={(v) => setDraft((d) => ({ ...d, endA: v }))}
        setDraftStartB={(v) => setDraft((d) => ({ ...d, startB: v }))}
        setDraftEndB={(v) => setDraft((d) => ({ ...d, endB: v }))}
        setDraftIncludeTestAccounts={(v) =>
          setDraft((d) => ({ ...d, includeTestAccounts: v }))
        }
        hasPendingChanges={hasPendingChanges}
        isFetching={isFetching}
        onApply={onApply}
        showEpic={showEpic}
        setShowEpic={setShowEpic}
      />

      {tab === "agency" ? (
        <AgencyTotals
          mode={applied.mode}
          granularity={applied.granularity}
          startA={applied.startA}
          endA={applied.endA}
          startB={applied.startB}
          endB={applied.endB}
          includeTestAccounts={applied.includeTestAccounts}
          showEpic={showEpic}
          onFetchingChange={setIsFetching}
        />
      ) : tab === "user" ? (
        <UserTotals
          mode={applied.mode}
          granularity={applied.granularity}
          startA={applied.startA}
          endA={applied.endA}
          startB={applied.startB}
          endB={applied.endB}
          includeTestAccounts={applied.includeTestAccounts}
          showEpic={showEpic}
          onFetchingChange={setIsFetching}
        />
      ) : tab === "role" ? (
        <RoleTotals
          mode={applied.mode}
          granularity={applied.granularity}
          startA={applied.startA}
          endA={applied.endA}
          startB={applied.startB}
          endB={applied.endB}
          includeTestAccounts={applied.includeTestAccounts}
          showEpic={showEpic}
          onFetchingChange={setIsFetching}
        />
      ) : (
        <PolicyInsights
          mode={applied.mode}
          startA={applied.startA}
          endA={applied.endA}
          startB={applied.startB}
          endB={applied.endB}
          includeTestAccounts={applied.includeTestAccounts}
          onFetchingChange={setIsFetching}
        />
      )}

      <AssistantBubble />
    </Page>
  );
};

export default MetricsPage;
